/**
 * LoginView
 * 
 * Authentication page with sign in and sign up forms.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import { GlassPanel, Button } from '../components/common'
import { APP_LOGO_TEXT, APP_TAGLINE, BRANDING } from '../config/branding'
import './LoginView.css'

export function LoginView() {
  const [mode, setMode] = useState('signin') // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  
  const { signIn, signUp, resetPassword, error, clearError } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    clearError()
    setMessage('')
    setLoading(true)

    try {
      if (mode === 'signin') {
        await signIn(email, password)
        navigate('/projects')
      } else if (mode === 'signup') {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match')
        }
        await signUp(email, password)
        navigate('/projects')
      } else if (mode === 'reset') {
        await resetPassword(email)
        setMessage('Password reset email sent! Check your inbox.')
        setMode('signin')
      }
    } catch (err) {
      // Error is handled by AuthContext
      console.error('Auth error:', err)
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (newMode) => {
    clearError()
    setMessage('')
    setMode(newMode)
  }

  return (
    <div className="login-view">
      <div className="login-background">
        <div className="login-grid" />
        <div className="login-glow login-glow-1" />
        <div className="login-glow login-glow-2" />
      </div>

      <motion.div
        className="login-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="login-logo">
          <h1>{APP_LOGO_TEXT}</h1>
          <p>{APP_TAGLINE}</p>
        </div>

        <GlassPanel className="login-panel" glow="cyan" padding="lg">
          <AnimatePresence mode="wait">
            <motion.form
              key={mode}
              onSubmit={handleSubmit}
              className="login-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <h2>
                {mode === 'signin' && 'Sign in'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'reset' && 'Reset Password'}
              </h2>

              {error && (
                <motion.div
                  className="login-error"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  {error}
                </motion.div>
              )}

              {message && (
                <motion.div
                  className="login-message"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  {message}
                </motion.div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              {mode !== 'reset' && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  />
                </div>
              )}

              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
              >
                {mode === 'signin' && 'Sign in'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'reset' && 'Send Reset Link'}
              </Button>

              <div className="login-links">
                {mode === 'signin' && (
                  <>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => switchMode('signup')}
                    >
                      Need an account? Sign up
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => switchMode('reset')}
                    >
                      Forgot password?
                    </button>
                  </>
                )}
                {mode === 'signup' && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => switchMode('signin')}
                  >
                    Already have an account? Sign in
                  </button>
                )}
                {mode === 'reset' && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => switchMode('signin')}
                  >
                    Back to sign in
                  </button>
                )}
              </div>
            </motion.form>
          </AnimatePresence>
        </GlassPanel>

        <p className="login-footer">
          {BRANDING.footer}
        </p>
      </motion.div>
    </div>
  )
}

export default LoginView

